/*
 * PORTFOLIO HEAT MAP — a VIEW over the authoritative venture_health records
 * (autonomous_os.compute_venture_health). One source, two views: this grid and
 * the Venture Health Map both read the exact records that drive agent cadence.
 * The former independent heat-map calculation and sensitivity weights were
 * removed in the merge.
 */
import { useEffect, useState } from "react";
import { heatmapApi } from "@/lib/api-client";
import { CATEGORY_META, fmtUSDFull } from "@/lib/format";
import { Grid3x3, RefreshCw, ShieldAlert, FileWarning, Radio } from "lucide-react";

const BAND_META = {
  healthy: { color: "#22C55E", label: "Healthy", bg: "rgba(34,197,94,0.10)" },
  watch: { color: "#EAB308", label: "Watch", bg: "rgba(234,179,8,0.10)" },
  warning: { color: "#F59E0B", label: "Warning", bg: "rgba(245,158,11,0.14)" },
  critical: { color: "#EF4444", label: "Critical", bg: "rgba(239,68,68,0.14)" },
};

export default function StatusHeatmap({ refreshKey }) {
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await heatmapApi.get();
      setCells(d.cells || []);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  const bands = cells.reduce((acc, c) => { acc[c.band] = (acc[c.band] || 0) + 1; return acc; }, {});

  return (
    <section className="uc-card p-6 uc-fade" data-testid="status-heatmap">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="uc-chip mb-2"><Grid3x3 size={11}/> STATUS HEATMAP</div>
          <h2 className="font-serif-display text-2xl">Portfolio Health Grid</h2>
          <p className="text-white/50 text-xs mt-1" data-testid="heatmap-source-line">
            Reads directly from Venture Health records — the same source that drives agent cadence.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {Object.entries(BAND_META).map(([k, m]) => (
            <div key={k} className="flex items-center gap-1.5 font-mono-uc text-[10px] uppercase tracking-wider text-white/60">
              <span className="uc-dot" style={{ background: m.color }}></span>{m.label} · {bands[k] || 0}
            </div>
          ))}
          <button data-testid="heatmap-refresh-btn" onClick={load} className="uc-chip hover:bg-white/5 transition-colors uc-focus">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""}/>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="heatmap-grid">
        {cells.map((c) => {
          const bm = BAND_META[c.band];
          const catMeta = CATEGORY_META[c.category];
          return (
            <div
              key={c.id}
              className="p-3 border border-white/10 hover:border-white/25 transition-colors relative overflow-hidden"
              style={{ background: bm.bg, borderLeft: `3px solid ${bm.color}` }}
              data-testid={`heatmap-cell-${c.id}`}
              title={`Health state: ${c.state} · agent cadence every ${c.cadence_cycles} cycle(s)`}
            >
              <div className="flex items-start justify-between mb-2 gap-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="uc-dot flex-shrink-0" style={{ background: catMeta?.color }}></span>
                  <span className="font-mono-uc text-[9px] uppercase tracking-wider text-white/50 truncate">{catMeta?.short}</span>
                </div>
                <div className="font-mono-uc text-[9px] uppercase tracking-wider" style={{ color: bm.color }} data-testid={`heatmap-band-${c.id}`}>
                  {c.band}
                </div>
              </div>
              <div className="text-sm text-white/90 leading-tight mb-2 line-clamp-2" data-testid={`heatmap-name-${c.id}`}>
                {c.name}
              </div>
              <div className="flex items-baseline justify-between mb-2">
                <span className="font-serif-display text-2xl" style={{ color: bm.color }} data-testid={`heatmap-score-${c.id}`}>{c.score}</span>
                <span className="font-mono-uc text-[10px] text-white/50">{fmtUSDFull(c.value)}</span>
              </div>
              <div className="w-full h-1 bg-white/10">
                <div className="h-full transition-all" style={{ width: `${c.score}%`, background: bm.color }}/>
              </div>
              {(c.red_docs > 0 || c.unresolved_events > 0 || c.recent_critical > 0) && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {c.red_docs > 0 && (
                    <span className="flex items-center gap-1 font-mono-uc text-[9px] text-red-400" title="Red-flagged docs">
                      <FileWarning size={9}/> {c.red_docs}
                    </span>
                  )}
                  {c.unresolved_events > 0 && (
                    <span className="flex items-center gap-1 font-mono-uc text-[9px] text-amber-400" title="Unresolved webhook events">
                      <Radio size={9}/> {c.unresolved_events}
                    </span>
                  )}
                  {c.recent_critical > 0 && (
                    <span className="flex items-center gap-1 font-mono-uc text-[9px] text-blue-400" title="Pending signals">
                      <ShieldAlert size={9}/> {c.recent_critical}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {cells.length === 0 && !loading && (
          <div className="col-span-full text-center text-white/40 text-sm italic py-8">
            No ventures to display yet.
          </div>
        )}
      </div>
    </section>
  );
}
